import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import CurrentUserData from "../queries/CurrentUserData";
import GetSurvey from "../queries/GetSurvey";
import UpdateUserSurvey from "../mutations/UpdateUserSurvey";
import UpdateUser from "../mutations/UpdateUser";
import LikertInputCustom from "./minorComponents/LikertInputCustom";
import BooleanCustom from "./minorComponents/BooleanCustom";
import PlatformQuestion from "./minorComponents/PlatformQuestion";
import SingleToggle from "./minorComponents/SingleToggle";
import StringInput from "./minorComponents/StringInput";
// reactstrap components
import { Button, Form, Modal } from "reactstrap";
import { useAppContext } from "../AppContext";

const SurveySectionOne = ({ survey, currentUserData }) => {
  const params = useParams();
  const navigate = useNavigate();

  const { displayToast, closeToast } = useAppContext();

  const [saveSurvey1] = useMutation(UpdateUserSurvey);
  const [saveUser] = useMutation(UpdateUser);

  const [showModal, setShowModal] = useState(false);
  const [variablesForModalClose, setVariablesForModalClose] = useState(null);

  const handleMutation = (mutationType, variables) => {
    if (mutationType === "user") {
      return saveUser({
        variables,
        refetchQueries: [{ query: CurrentUserData }],
      })
        .then((result) => {
          console.log("User Mutation result: ", result);
        })
        .catch((error) => {
          console.log("User Mutation error: ", error);
        });
    } else if (mutationType === "survey") {
      return saveSurvey1({
        variables,
        refetchQueries: [
          { query: CurrentUserData },
          { query: GetSurvey, variables: { surveyId: params.surveyId } },
        ],
      })
        .then((result) => {
          console.log("User Mutation result: ", result);
        })
        .catch((error) => {
          console.log("User Mutation error: ", error);
        });
    }
  };

  const [errors, setErrors] = useState([]);
  const questions = [
    {
      key: "current_tl_or_sme", // this is the key that will be used by both the backend for storing/retrieving and front end for nowing what to put where
      type: "boolean", // this is how it'll know what type of input to display, boolean toggle, likert or custom likert
      default: false, // this is to let it know what to default to... false is best
      table: "user", // this is to let it know which table this should be stored on, either user or survey. MUST MAKE SURE EVERYTHING CAN ACTUALLY SUPPORT IT DO NOT JUST RANDOMLY ADD NEW THINGS OR SAVE WILL THROW ERRORS
      text: "Are you a current Target Language (TL) Instructor or Subject Matter Expert (SME)?", // this is the question text that'll be displayed
      trueOption: "Yes",
      falseOption: "No",
      required: true,
      errorText: "Please make a selection.",
    },
    {
      key: "tl_sme_lot",
      type: "likertInputCustom",
      default: null,
      table: "user",
      text: "If you are a TL Instructor or SME, the length of time in your present position is:",
      fields: [
        // custome likert will need an array of fields, the text can be changed when ever if a spelling error and can add/remove new values as needed. DO NOT CHANGE VALUE OR ALREADY ANSWERED WILL BE LOST UNLESS YOU'RE SURE
        { text: "Not Applicable", value: "na" },
        { text: "Less than 5 years", value: "<5" },
        { text: "5 - 10 years", value: "5-10" },
        { text: "More than 10 years", value: ">10" },
      ],
      requiredIf: "current_tl_or_sme",
      errorText: "Please make a selection.",
    },
    {
      key: "current_former_fao",
      type: "boolean",
      default: false,
      table: "user",
      text: "Are you a current or former Foreign Area Officer (FAO)?",
      trueOption: "Yes",
      falseOption: "No",
      required: true,
      errorText: "Please make a selection.",
    },
    {
      key: "fao_rank",
      type: "likertInputCustom",
      default: null,
      table: "user",
      text: "If you are a current FAO, your military rank is:",
      fields: [
        // custome likert will need an array of fields, the text can be changed when ever if a spelling error and can add/remove new values as needed. DO NOT CHANGE VALUE OR ALREADY ANSWERED WILL BE LOST UNLESS YOU'RE SURE
        { text: "O-4", value: "o4" },
        { text: "O-5", value: "o5" },
        { text: "O-6", value: "o6" },
        { text: "O-7 or Above", value: "o7orUp" },
      ],
      requiredIf: "current_former_fao",
      errorText: "Please make a selection.",
    },
    {
      key: "fao_lot",
      type: "likertInputCustom",
      default: null,
      table: "user",
      text: "If you are a current FAO, the length of time in your present position is:",
      fields: [
        // custome likert will need an array of fields, the text can be changed when ever if a spelling error and can add/remove new values as needed. DO NOT CHANGE VALUE OR ALREADY ANSWERED WILL BE LOST UNLESS YOU'RE SURE
        { text: "Not Applicable", value: "na" },
        { text: "Less than 6 Months", value: "less6m" },
        { text: "5 months to 1 year", value: "5m-1y" },
        { text: "1 to 2 years", value: "1y-2y" },
        { text: "2 to 3 years", value: "2y-3y" },
        { text: "More than 3 years", value: "3yUp" },
      ],
      requiredIf: "current_former_fao",
      errorText: "Please make a selection.",
    },
    {
      key: "fao_assignment",
      type: "boolean",
      default: false,
      table: "user",
      text: "If you are a current/former FAO, did you serve in at least one operational FAO assignment (overseas)?",
      trueOption: "Yes",
      falseOption: "No",
      requiredIf: "current_former_fao",
      errorText: "Please make a selection.",
    },
    {
      key: "ilr",
      type: "boolean",
      default: false,
      table: "user",
      text: "If you are a current/former FAO, do you have an identified ILR proficiency level?",
      trueOption: "Yes",
      falseOption: "No",
      requiredIf: "current_former_fao",
      errorText: "Please make a selection.",
    },
    {
      key: "ilr_reading",
      type: "likertInputCustom",
      default: null,
      table: "user",
      text: "Please indicate your ILR reading proficiency level:",
      fields: [
        { text: "N/A", value: "N/A" },
        { text: "0", value: "0" },
        { text: "0+", value: "0+" },
        { text: "1", value: "1" },
        { text: "1+", value: "1+" },
        { text: "2", value: "2" },
        { text: "2+", value: "2+" },
        { text: "3", value: "3" },
        { text: "3+", value: "3+" },
        { text: "4", value: "4" },
        { text: "4+", value: "4+" },
        { text: "5", value: "5" },
      ],
      requiredIf: "ilr",
      errorText: "Please make a selection.",
    },
    {
      key: "ilr_listening",
      type: "likertInputCustom",
      default: null,
      table: "user",
      text: "Please indicate your ILR listening proficiency level:",
      fields: [
        { text: "N/A", value: "N/A" },
        { text: "0", value: "0" },
        { text: "0+", value: "0+" },
        { text: "1", value: "1" },
        { text: "1+", value: "1+" },
        { text: "2", value: "2" },
        { text: "2+", value: "2+" },
        { text: "3", value: "3" },
        { text: "3+", value: "3+" },
        { text: "4", value: "4" },
        { text: "4+", value: "4+" },
        { text: "5", value: "5" },
      ],
      requiredIf: "ilr",
      errorText: "Please make a selection.",
    },
    {
      key: "dlpt",
      type: "boolean",
      default: false,
      table: "user",
      text: "If you are a current/former FAO, do you have DLPT certification?",
      trueOption: "Yes",
      falseOption: "No",
      requiredIf: "current_former_fao",
      errorText: "Please make a selection.",
    },
    {
      key: "dlpt_reading",
      type: "likertInputCustom",
      default: null,
      table: "user",
      text: "Please indicate your reading comprehension level:",
      fields: [
        { text: "N/A", value: "N/A" },
        { text: "0", value: "0" },
        { text: "0+", value: "0+" },
        { text: "1", value: "1" },
        { text: "1+", value: "1+" },
        { text: "2", value: "2" },
        { text: "2+", value: "2+" },
        { text: "3", value: "3" },
        { text: "3+", value: "3+" },
        { text: "4", value: "4" },
      ],
      requiredIf: "dlpt",
      errorText: "Please make a selection.",
    },
    {
      key: "dlpt_listening",
      type: "likertInputCustom",
      default: null,
      table: "user",
      text: "Please indicate your listening comprehension level:",
      fields: [
        { text: "N/A", value: "N/A" },
        { text: "0", value: "0" },
        { text: "0+", value: "0+" },
        { text: "1", value: "1" },
        { text: "1+", value: "1+" },
        { text: "2", value: "2" },
        { text: "2+", value: "2+" },
        { text: "3", value: "3" },
        { text: "3+", value: "3+" },
        { text: "4", value: "4" },
      ],
      requiredIf: "dlpt",
      errorText: "Please make a selection.",
    },
    {
      key: "opi",
      type: "boolean",
      default: false,
      table: "user",
      text: "If you are a current/former FAO, do you have OPI certification?",
      trueOption: "Yes",
      falseOption: "No",
      requiredIf: "current_former_fao",
      errorText: "Please make a selection.",
    },
    {
      key: "opi_speaking",
      type: "likertInputCustom",
      default: null,
      table: "user",
      text: "If you have OPI certification, what level is it for Speaking:",
      fields: [
        { text: "0", value: "0" },
        { text: "0+", value: "0+" },
        { text: "1", value: "1" },
        { text: "1+", value: "1+" },
        { text: "2", value: "2" },
        { text: "2+", value: "2+" },
        { text: "3", value: "3" },
        { text: "3+", value: "3+" },
        { text: "4", value: "4" },
        { text: "4+", value: "4+" },
        { text: "5", value: "5" },
      ],
      requiredIf: "opi",
      errorText: "Please make a selection.",
    },
    {
      key: "other",
      type: "boolean",
      default: false,
      table: "user",
      text: "If you are a current/former FAO, do you have another certification?",
      trueOption: "Yes",
      falseOption: "No",
      requiredIf: "current_former_fao",
      errorText: "Please make a selection.",
    },
    {
      key: "other_string",
      type: "string",
      default: false,
      table: "user",
      text: "What other certification do you have? (fill in)",
      requiredIf: "other",
      errorText: "Please make a selection.",
    },
    {
      key: "other_reading",
      type: "likertInputCustom",
      default: null,
      table: "user",
      text: "If you have another certification, what level is it for Reading:",
      fields: [
        { text: "0", value: "0" },
        { text: "0+", value: "0+" },
        { text: "1", value: "1" },
        { text: "1+", value: "1+" },
        { text: "2", value: "2" },
        { text: "2+", value: "2+" },
        { text: "3", value: "3" },
        { text: "3+", value: "3+" },
        { text: "4", value: "4" },
        { text: "4+", value: "4+" },
        { text: "5", value: "5" },
      ],
      requiredIf: "other",
      errorText: "Please make a selection.",
    },
    {
      key: "other_listening",
      type: "likertInputCustom",
      default: null,
      table: "user",
      text: "If you have another certification, what level is it for Listening:",
      fields: [
        { text: "0", value: "0" },
        { text: "0+", value: "0+" },
        { text: "1", value: "1" },
        { text: "1+", value: "1+" },
        { text: "2", value: "2" },
        { text: "2+", value: "2+" },
        { text: "3", value: "3" },
        { text: "3+", value: "3+" },
        { text: "4", value: "4" },
        { text: "4+", value: "4+" },
        { text: "5", value: "5" },
      ],
      requiredIf: "other",
      errorText: "Please make a selection.",
    },
    {
      key: "other_speaking",
      type: "likertInputCustom",
      default: null,
      table: "user",
      text: "If you have another certification, what level is it for Speaking:",
      fields: [
        { text: "0", value: "0" },
        { text: "0+", value: "0+" },
        { text: "1", value: "1" },
        { text: "1+", value: "1+" },
        { text: "2", value: "2" },
        { text: "2+", value: "2+" },
        { text: "3", value: "3" },
        { text: "3+", value: "3+" },
        { text: "4", value: "4" },
        { text: "4+", value: "4+" },
        { text: "5", value: "5" },
      ],
      requiredIf: "other",
      errorText: "Please make a selection.",
    },
  ];
  const [values, setValues] = useState(() => {
    return initializeValues();
  });

  function initializeValues() {
    let newValues = {};
    questions.forEach((question, index) => {
      if (question.table === "survey") {
        if (question.type === "platformQuestion") {
          question.categories.forEach((category, cIndex) => {
            category.subcategories.forEach((subcategory, sIndex) => {
              newValues[subcategory.key] = survey[subcategory.key];
            });
          });
        } else {
          newValues[question.key] = survey[question.key];
        }
      } else {
        newValues[question.key] = survey.user[question.key];
      }
    });
    return newValues;
  }

  function showToast(success, message) {
    const toast = {
      title: success ? "Success" : "Error",
      color: success ? "bg-success" : "bg-danger",
      icon: success ? "check-bold" : "fat-remove",
      message,
    };

    displayToast(toast);

    setTimeout(() => {
      closeToast();
    }, 10000);
  }

  function onSubmit() {
    let newErrors = [];
    let userVariables = { userId: currentUserData.id };
    let surveyVariables = { surveyId: params.surveyId };

    // loop through all questions, if any are required but not completed then log it to newErrors while also storing the new variables in order to update DB
    questions.forEach((question, index) => {
      if (
        question.required &&
        (typeof values[question.key] == "undefined" ||
          values[question.key] == null)
      ) {
        newErrors.push({ name: question.key, text: question.errorText });
      } else if (
        typeof question.requiredIf != "undefined" &&
        question.requiredIf != null
      ) {
        if (
          typeof values[question.requiredIf] != "undefined" &&
          values[question.requiredIf] === true
        ) {
          if (
            typeof values[question.key] == "undefined" ||
            values[question.key] == null ||
            values[question.key] === ""
          ) {
            newErrors.push({ name: question.key, text: question.errorText });
          }
        }
      } else if (question.type === "platformQuestion") {
        let chosen = null;
        question.categories.forEach((category, cIndex) => {
          category.subcategories.forEach((subcategory, sIndex) => {
            if (
              typeof values[subcategory.key] !== "undefined" &&
              values[subcategory.key] !== null &&
              values[subcategory.key] !== null &&
              values[subcategory.key] !== false &&
              values[subcategory.key] !== ""
            ) {
              chosen = subcategory.key;
            }
          });
        });

        if (chosen == null) {
          newErrors.push({
            name: "platformQuestion",
            text: "Please select at least thing you used",
          });
        }
      }

      if (question.table === "survey") {
        if (question.type === "platformQuestion") {
          question.categories.forEach((category, cIndex) => {
            category.subcategories.forEach((subcategory, sIndex) => {
              surveyVariables[subcategory.key] = values[subcategory.key];
            });
          });
        } else {
          surveyVariables[question.key] = values[question.key];
        }
      } else {
        userVariables[question.key] = values[question.key];
      }
    });

    setErrors(newErrors);

    if (newErrors.length === 0) {
      const dateToStore = new Date();
      userVariables["section_1_completed_date"] = JSON.stringify(dateToStore);
    } else {
      console.log("new errors: ", newErrors);
      userVariables["section_1_completed_date"] = "";
    }

    // possible situations upon submission.
    // All fields are answered and the other survey is done
    // All fields are answered but the other survey is NOT done
    // not all fields are answered but the other survey is done
    // Not all fields are answered and the other survey is NOT done
    if (
      survey.section_3_completed_date !== null &&
      userVariables["section_1_completed_date"] !== ""
    ) {
      setShowModal(true);
      setVariablesForModalClose({
        userVariables,
        surveyVariables,
      });
    } else if (userVariables["section_1_completed_date"] !== "") {
      console.log("this survey is complete but other one isn't");
      console.log("got: ", userVariables);
      Promise.all([
        handleMutation("user", userVariables),
        handleMutation("survey", surveyVariables),
      ]).then(() => {
        showToast(
          true,
          "You have completed and saved this section. Please select another section at the top of this page.",
        );
      });
      setVariablesForModalClose(null);
    } else if (survey.section_3_completed_date !== null) {
      // console.log("this survey has missing answers but the other one is completed")
      Promise.all([
        handleMutation("user", userVariables),
        handleMutation("survey", surveyVariables),
      ]).then(() => {
        showToast(
          false,
          "Your answers are saved. However, one or more required items are not completed. Please complete items noted in red.",
        );
      });
      setVariablesForModalClose(null);
    } else {
      // console.log('neither survey is completed')
      Promise.all([
        handleMutation("user", userVariables),
        handleMutation("survey", surveyVariables),
      ]).then(() => {
        showToast(
          false,
          "Your answers are saved. However, one or more required items are not completed. Please complete items noted in red.",
        );
      });
      setVariablesForModalClose(null);
    }
  }

  function handleConfirm(choice) {
    setShowModal(false);

    const actualUser = variablesForModalClose.userVariables;
    const actualSurvey = variablesForModalClose.surveyVariables;
    if (choice) {
      const dateToStore = new Date();
      actualSurvey["completed_date"] = JSON.stringify(dateToStore);
    }

    Promise.all([
      handleMutation("user", actualUser),
      handleMutation("survey", actualSurvey),
    ])
      .then(() => {
        if (choice) {
          navigate("/surveys", { replace: true });
        } else {
          showToast(
            true,
            "Both sections are saved. You can make any last changes and submit again or you can return to the main surveys table and finalize submission there.",
          );
        }
      })
      .catch((error) => {
        console.log("mutation failed: ", error);
      });
  }

  function handleChange(name, newValue) {
    const updatedValues = { ...values };

    updatedValues[name] = newValue;

    setValues(updatedValues);
  }

  function displayQuestions() {
    console.log("values: ", values);
    let qIndex = null;
    return questions.map((question) => {
      let thisQuestionError = {};
      if (errors.length > 0) {
        thisQuestionError = errors.find((error) => {
          return error.name === question.key;
        });
      }

      let showQuestion = false;
      if (!question.requiredIf) {
        showQuestion = true;
      } else if (question.requiredIf && values[question.requiredIf]) {
        console.log("this has a required if: ", question);
        showQuestion = true;
      }

      if (showQuestion) {
        // either set qIndex or increase it
        qIndex = qIndex !== null ? qIndex + 1 : 0;
        if (question.type === "boolean") {
          return (
            <BooleanCustom
              handleChange={(name, newValue) => handleChange(name, newValue)}
              label={question.text}
              name={question.key}
              currentValue={values[question.key]}
              trueOption={question.trueOption}
              falseOption={question.falseOption}
              key={`row_${qIndex}`}
              error={thisQuestionError}
              qIndex={qIndex + 1}
            />
          );
        } else if (question.type === "platformQuestion") {
          let content = JSON.parse(JSON.stringify(question));
          content.categories.forEach((category, cIndex) => {
            category.subcategories.forEach((subcategory, sIndex) => {
              let returnObject = subcategory;
              returnObject.currentValue = values[subcategory.key];

              content.categories[cIndex].subcategories[sIndex] = returnObject;
            });
          });

          return (
            <PlatformQuestion
              handleChange={(name, newValue) => handleChange(name, newValue)}
              label={question.text}
              name={question.key}
              key={`row_${qIndex}`}
              content={content}
              error={thisQuestionError}
              qIndex={qIndex + 1}
            />
          );
        } else if (question.type === "singleToggle") {
          console.log("going to display single toggle");
          return (
            <SingleToggle
              handleChange={(name, newValue) => handleChange(name, newValue)}
              label={question.text}
              name={question.key}
              currentValue={values[question.key]}
              key={`row_${qIndex}`}
              error={thisQuestionError}
              qIndex={qIndex + 1}
            />
          );
        } else if (question.type === "string") {
          return (
            <StringInput
              handleChange={(name, newValue) => handleChange(name, newValue)}
              label={question.text}
              name={question.key}
              currentValue={values[question.key]}
              key={`row_${qIndex}`}
              error={thisQuestionError}
              qIndex={qIndex + 1}
            />
          );
        } else if (question.type === "likertInputCustom") {
          return (
            <LikertInputCustom
              handleChange={(name, newValue) => handleChange(name, newValue)}
              label={question.text}
              name={question.key}
              currentValue={values[question.key]}
              values={question.fields}
              key={`row_${qIndex}`}
              error={thisQuestionError}
              qIndex={qIndex + 1}
            />
          );
        } else {
          return (
            <div key={`row_${qIndex}`}>
              <strong>{question.text} lacks a valid type...</strong>
            </div>
          );
        }
      } else {
        console.log("told to not show question...");
      }
    });
  }

  return (
    <div className="pt-3">
      <h2>Section 1</h2>
      <p>
        <span className="font-weight-bold">Instructions:</span> Please complete
        the following information.
      </p>
      <Form>
        {displayQuestions()}
        {errors.length > 0 && (
          <div className="text-danger mb-3">
            Your answers are saved. However, one or more required items are not
            completed. Please complete items noted in red.
          </div>
        )}
        <Button
          color="primary"
          onClick={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          Save Section 1
        </Button>
      </Form>
      <Modal
        className="modal-dialog-centered"
        isOpen={showModal}
        toggle={() => setShowModal(!showModal)}
      >
        <div className="modal-header">
          <h5 className="modal-title" id="exampleModalLabel">
            Submit Completed Survey
          </h5>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={() => setShowModal(false)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body">
          All required survey questions have been answered. Are you ready to
          submit this survey? After the survey is submitted, no changes can be
          made. Make and save any desired changes before selecting Submit.
        </div>
        <div className="modal-footer">
          <Button color="secondary" onClick={() => handleConfirm(false)}>
            Cancel Submission
          </Button>
          <Button color="primary" onClick={() => handleConfirm(true)}>
            Submit Survey
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default SurveySectionOne;
